import {
  type Dispatch,
  forwardRef,
  type MouseEventHandler,
  type SetStateAction,
  useContext,
  useState,
} from 'react';
import ChevronIcon from '@assets/img/icons/chevron-2.svg?react';
import CloseIcon from '@assets/img/icons/close.svg?react';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { type Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { convertRemToPixels } from '@/common/lib/convertRemtoPx';
import { getCssVar } from '@/common/lib/cssVar';
import { getImage } from '@/common/lib/images';
import { type GoodsType } from '@/common/types/goods';
import { type ProductData } from '@/common/types/workshopType';
import ContractBackground from '@/components/common/ContractBackground/ContractBackground';
import { MotionButton } from '@/components/ui/Button/Button';
import { Picture } from '@/components/ui/Picture/Picture';
import { GoodsDetailContext } from '@/components/Workshop/WorkshopGoods/context';
import { useAppSelector } from '@/redux/store';

import { OneUnitInfo } from './OneUnitInfo';

import css from './styles.module.scss';

interface GoodsDetailProps {
  name: GoodsType;
  // '9rem': number | string;
  // isActive: boolean;
  costPerUnit?: number;
  productionRate?: number;
  workshopId: string;
  className?: string;
  detailIsOpen: boolean;
  setDetailIsOpen: Dispatch<SetStateAction<boolean>>;
  slideList?: ProductData[];
  firstSwiper?: SwiperInstance;
  initialSlide: number;
}

const GoodsDetail = forwardRef<HTMLDivElement, GoodsDetailProps>(
  (
    {
      name,
      costPerUnit,
      productionRate,
      workshopId,
      className,
      slideList,
      firstSwiper,
      initialSlide,
    },
    ref,
  ) => {
    const { setIsOpen, isOpen, animateTitle } = useContext(GoodsDetailContext);
    const [secondSwiper, setSecondSwiper] = useState<SwiperInstance>();
    const productsOfDay = useAppSelector(
      (state) => state.contract.productOfDay,
    );

    const closeDetail: MouseEventHandler<HTMLButtonElement> = (event) => {
      event.stopPropagation();
      animateTitle();
      setIsOpen(false);
    };

    const nextSlide: MouseEventHandler<HTMLButtonElement> = (event) => {
      event.stopPropagation();
      firstSwiper?.slideNext();
      secondSwiper?.slideNext();
    };

    const prevSlide: MouseEventHandler<HTMLButtonElement> = (event) => {
      event.stopPropagation();
      firstSwiper?.slidePrev();
      secondSwiper?.slidePrev();
    };

    const desktopContainerWidth = parseFloat(
      getCssVar('--desktop-container-content-width'),
    );

    const width = desktopContainerWidth - convertRemToPixels(2);

    return (
      <div
        ref={ref}
        style={{ width: '9rem', height: '9rem' }}
        className={cn('relative', className)}
      >
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ y: '-50%', x: '-50%' }}
              animate={{
                width,
                height: 'auto',
                y: '-30%',
                x: '-50%',
              }}
              exit={{ width: '9rem', height: '9rem', y: '-50%', x: '-50%' }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              style={{ width: '9rem', height: '9rem' }}
              className={css.wrapper}
            >
              <div className="relative w-full h-full flex flex-col items-center">
                <MotionButton
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transition: { delay: 0.1, duration: 0.3 },
                  }}
                  exit={{ opacity: 0, transition: { duration: 0 } }}
                  variant="transparent"
                  className="!w-auto p-2 absolute top-0 right-0 z-[3]"
                  onClickCapture={closeDetail}
                >
                  <CloseIcon className={cn('w-6 h-6 text-white/40')} />
                </MotionButton>
                <motion.div
                  initial={{ y: 0 }}
                  animate={{ y: '3.25rem' }}
                  exit={{ y: 0 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  className="w-full relative"
                >
                  <MotionButton
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      transition: { delay: 0.1, duration: 0.3 },
                    }}
                    exit={{ opacity: 0, transition: { duration: 0 } }}
                    onClickCapture={prevSlide}
                    variant="transparent"
                    className="absolute z-[2] !w-auto p-4 left-4 top-1/2 -translate-y-1/2"
                  >
                    <ChevronIcon className="text-white/40" />
                  </MotionButton>
                  <Swiper
                    onSwiper={setSecondSwiper}
                    slidesPerView="auto"
                    spaceBetween="100%"
                    loop={false}
                    centeredSlides={true}
                    initialSlide={initialSlide}
                  >
                    {slideList?.map(({ name }) => {
                      const goodsImage = getImage({
                        name: name,
                        field: 'icons',
                      });

                      const foundProductOfDay = productsOfDay?.find(
                        (product) => product.name === name,
                      );

                      return (
                        <SwiperSlide
                          key={name}
                          style={{ width: '9rem', height: '9rem' }}
                          className="flex items-center justify-center"
                        >
                          <div className="size-[9rem] rounded-lg relative">
                            {!!foundProductOfDay && (
                              <ContractBackground
                                className="absolute h-[40%] w-full left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[-1]"
                                type={
                                  foundProductOfDay.type === 'premium'
                                    ? 'purple'
                                    : 'green'
                                }
                              />
                            )}
                            <Picture
                              className="block size-full"
                              img={goodsImage?.img}
                              webp={goodsImage?.webp}
                              style={{ width: '9rem', height: '9rem' }}
                              alt={name}
                            />
                          </div>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                  <MotionButton
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      transition: { delay: 0.1, duration: 0.3 },
                    }}
                    exit={{ opacity: 0, transition: { duration: 0 } }}
                    onClickCapture={nextSlide}
                    variant="transparent"
                    className="absolute z-[2] !w-auto p-4 right-4 top-1/2 -translate-y-1/2"
                  >
                    <ChevronIcon className="rotate-180 text-white/40" />
                  </MotionButton>
                </motion.div>

                <OneUnitInfo
                  detailIsOpen={isOpen}
                  costPerUnit={costPerUnit}
                  productionRate={productionRate}
                  goodsName={name}
                  workshopId={workshopId}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

export { GoodsDetail };
